import { Injectable, computed, signal } from '@angular/core';

const STORAGE_KEY = 'ims-report-date-range';

@Injectable({ providedIn: 'root' })
export class ReportDateRangeService {
  readonly draftStartDate = signal(this.toInputDate(this.startOfMonth(new Date())));
  readonly draftEndDate = signal(this.toInputDate(new Date()));
  readonly appliedStartDate = signal(this.draftStartDate());
  readonly appliedEndDate = signal(this.draftEndDate());

  readonly isDraftValid = computed(() =>
    this.isRangeValid(this.draftStartDate(), this.draftEndDate())
  );

  readonly appliedLabel = computed(() =>
    this.formatRange(this.appliedStartDate(), this.appliedEndDate())
  );

  readonly appliedDayCount = computed(() =>
    this.daySpan(this.appliedStartDate(), this.appliedEndDate())
  );

  constructor() {
    this.restore();
  }

  apply(): boolean {
    if (!this.isDraftValid()) {
      return false;
    }

    this.appliedStartDate.set(this.draftStartDate());
    this.appliedEndDate.set(this.draftEndDate());
    this.persist();
    return true;
  }

  setPreset(preset: 'today' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth'): void {
    const today = new Date();
    let start: Date;
    let end: Date = today;

    switch (preset) {
      case 'today':
        start = today;
        break;
      case 'last7':
        start = new Date(today);
        start.setDate(start.getDate() - 6);
        break;
      case 'last30':
        start = new Date(today);
        start.setDate(start.getDate() - 29);
        break;
      case 'thisMonth':
        start = this.startOfMonth(today);
        break;
      case 'lastMonth':
        start = this.startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
    }

    this.draftStartDate.set(this.toInputDate(start));
    this.draftEndDate.set(this.toInputDate(end));
    this.apply();
  }

  private isRangeValid(start: string, end: string): boolean {
    return Boolean(start && end && start <= end);
  }

  private formatRange(start: string, end: string): string {
    const formatter = new Intl.DateTimeFormat('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    return `${formatter.format(this.parseInputDate(start))} – ${formatter.format(this.parseInputDate(end))}`;
  }

  private daySpan(start: string, end: string): number {
    const startDate = this.parseInputDate(start);
    const endDate = this.parseInputDate(end);
    const msPerDay = 24 * 60 * 60 * 1000;

    return Math.floor((endDate.getTime() - startDate.getTime()) / msPerDay) + 1;
  }

  private parseInputDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private toInputDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private persist(): void {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        draftStartDate: this.draftStartDate(),
        draftEndDate: this.draftEndDate(),
        appliedStartDate: this.appliedStartDate(),
        appliedEndDate: this.appliedEndDate()
      })
    );
  }

  private restore(): void {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const saved = JSON.parse(raw) as {
        draftStartDate?: string;
        draftEndDate?: string;
        appliedStartDate?: string;
        appliedEndDate?: string;
      };

      if (saved.draftStartDate && saved.draftEndDate) {
        this.draftStartDate.set(saved.draftStartDate);
        this.draftEndDate.set(saved.draftEndDate);
      }

      if (saved.appliedStartDate && saved.appliedEndDate) {
        this.appliedStartDate.set(saved.appliedStartDate);
        this.appliedEndDate.set(saved.appliedEndDate);
      }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }
}
